// Drawer lateral que hospeda la Persona canónica. Real: carga /personas/{id}/ficha. Demo: recibe la
// ficha ya construida del fixture (NUNCA llama backend). El Radar/Población quedan visibles detrás.
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fichaFromApi } from "@/lib/persona/contract";
import PersonaFicha from "@/components/persona/PersonaFicha";
import RegistrarContactoModal from "@/components/persona/RegistrarContactoModal";
import PlanRespuestaDialog from "@/components/persona/PlanRespuestaDialog";
import { useAuth } from "@/context/AuthContext";
import { X, FlaskConical, Loader2, ClipboardList } from "lucide-react";

export default function PersonaDrawer({ open, onClose, personaId, demoFicha = null, isDemo = false }) {
  const navigate = useNavigate();
  const { isSupervisor, isExternal } = useAuth();
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contactoOpen, setContactoOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    if (isDemo) { setFicha(demoFicha); return; }
    if (!personaId) return;
    setLoading(true);
    try { const { data } = await api.get(`/personas/${personaId}/ficha`); setFicha(fichaFromApi(data)); }
    catch { setFicha(null); toast.error("No se pudo cargar la ficha."); }
    finally { setLoading(false); }
  }, [isDemo, demoFicha, personaId]);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);
  if (!open) return null;

  const onConfirmContacto = async (payload) => {
    if (isDemo) {
      setFicha((f) => f ? { ...f, ultimos_hechos: [{ clase: "contacto", label: `Contacto${payload.relacion ? ` · ${payload.relacion}` : ""}`, sub: payload.motivo, hora: new Date().toISOString(), hace: "hace segundos" }, ...f.ultimos_hechos] } : f);
      setContactoOpen(false);
      toast.info("Datos de demostración: el contacto sólo se muestra localmente (no se guarda).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/personas/${personaId}/contacto-operativo`, payload);
      toast.success("Contacto registrado.");
      setContactoOpen(false);
      await cargar();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verHistorial = () => {
    const pid = ficha?.persona_id || personaId;
    if (isDemo) { navigate("/bitacora"); toast.info("Bitácora (demo)."); }
    // GC92 · la enfermería institucional (external) va a la BITÁCORA OPERATIVA, su superficie
    // autorizada; las cuentas internas conservan su Bitácora en vivo de siempre.
    else if (pid) navigate(isExternal ? `/bitacora-operativa?persona_id=${pid}` : `/bitacora?persona_id=${pid}`);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <aside data-testid="persona-drawer" className="fixed right-0 top-0 z-50 flex h-full w-[94%] max-w-[420px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-[#263B5D] dark:bg-[#0B111E]">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-[#263B5D]">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#012472] dark:text-[#80B3FF]">
            Ficha de la persona
            {isDemo && <span className="inline-flex items-center gap-1 rounded bg-[#9B2C2C]/10 px-1.5 py-0.5 text-[10px] text-[#9B2C2C]"><FlaskConical className="h-3 w-3" /> DEMO</span>}
          </p>
          <button data-testid="persona-drawer-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-[#192C47]"><X className="h-4 w-4" /></button>
        </div>
        {isSupervisor && !isDemo && personaId && (
          <div className="border-b border-slate-100 px-4 py-2 dark:border-[#263B5D]">
            <button data-testid="persona-plan-respuesta-btn" onClick={() => setPlanOpen(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#012472]/30 bg-[#012472]/5 px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#012472] transition-colors hover:bg-[#012472]/10 dark:border-[#80B3FF]/30 dark:text-[#80B3FF]">
              <ClipboardList className="h-3.5 w-3.5" /> Plan de respuesta
            </button>
          </div>
        )}
        {/* La reserva de carril inferior frente a la burbuja de alertas vive en PersonaFicha. */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <PersonaFicha ficha={ficha} onRegistrarContacto={() => setContactoOpen(true)} onVerHistorial={verHistorial} />
          )}
        </div>
      </aside>
      <RegistrarContactoModal open={contactoOpen} onOpenChange={setContactoOpen} onConfirm={onConfirmContacto} submitting={busy} />
      {isSupervisor && !isDemo && <PlanRespuestaDialog open={planOpen} onOpenChange={setPlanOpen} personaId={personaId} />}
    </>
  );
}
